import React, { useEffect, useState } from 'react';
import { Pagination } from '@mui/material';
import _ from 'lodash';
import { IColumnType } from 'components/Table/types';
import BasicTable from 'components/Table';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import NoInfo from 'components/NoInfo';
import { PageContentContainer, PageContentTable } from '../PageContentContainer';
import {
  ApiNeedToModifiedChanges,
  AuditDefaultSort,
  AuditId,
  AuditTableColumn,
  AuditTableColumnTitles,
  IAudit
} from 'constants/audit.constants';
import { getAudits } from 'state/reducers/audit.reducers';
import { orderBy } from 'constants/ui.constants';
import { getSites } from 'state/reducers/site.reducers';
import { getPatients } from 'state/reducers/patients.reducers';
import { getModifiedAuditsChanges } from './Audit.services';
import { getAllUsers } from 'state/reducers/user.reducers';

const AuditPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchValue, setSearchValue] = useState<string>('');
  const [pageNumber, setPageNumber] = useState(1);
  const [orderColumn, setOrderColumn] = useState('');
  const [modifiedAudits, setModifiedAudits] = useState<IAudit[]>([]);
  const [orderDirection, setOrderDirection] = useState(orderBy.DESC);
  const { audits, totalNumber, loading, error } = useAppSelector((state) => state.audit);
  const { sites } = useAppSelector((state) => state.sites);
  const { patients } = useAppSelector((state) => state.patients);
  const { users } = useAppSelector((state) => state.user);
  const [siteIdName, setSiteIdName] = useState<Record<number, string>>({});
  const [patientIdName, setPatientIdName] = useState<Record<string, string>>({});
  const [userIdName, setUserIdName] = useState<Record<number, string>>({});
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);

  useEffect(() => {
    dispatch(getSites());
    dispatch(getPatients());
    dispatch(getAllUsers());
  }, [dispatch]);

  useEffect(() => {
    if (sites?.length && patients?.length && users.length) {
      const siteIdName = sites?.reduce((acc: Record<number, string>, site) => {
        acc[site.id!] = site.name!;
        return acc;
      }, {});

      const patientIdName = patients?.reduce((acc: Record<string, string>, patient) => {
        acc[patient.guid!] = `${patient.firstName} ${patient.lastName}`;
        return acc;
      }, {});

      const userIdName = users?.reduce((acc: Record<number, string>, user) => {
        acc[user.id!] = `${user.firstName} ${user.lastName}`;
        return acc;
      }, {});

      setSiteIdName(siteIdName);
      setPatientIdName(patientIdName);
      setUserIdName(userIdName);
    }
  }, [sites, patients, users]);

  useEffect(() => {
    updateModifiedAudits();
  }, [audits, patientIdName, siteIdName, userIdName]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
      setPageNumber(1);
    }, 800);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  useEffect(() => {
    dispatch(
      getAudits({
        pageNumber,
        orderColumn,
        orderDirection,
        searchValue: debouncedSearchValue || undefined
      })
    );
  }, [pageNumber, orderColumn, orderDirection, debouncedSearchValue, dispatch]);

  const getAuditChanges = (audit: IAudit): string => {
    if (audit?.changes && audit.changes !== '{}') {
      if (ApiNeedToModifiedChanges.includes(audit.api)) {
        return getModifiedAuditsChanges(audit.changes, patientIdName, siteIdName, userIdName);
      } else {
        return audit.changes;
      }
    }
    return '{}';
  };

  const updateModifiedAudits = () => {
    if (
      !!Object.keys(patientIdName).length &&
      !!Object.keys(siteIdName).length &&
      !!Object.keys(userIdName).length &&
      !!audits?.length
    ) {
      const modified = audits.map((audit) => ({
        ...audit,
        changes: getAuditChanges(audit),
        ...getInternalData(audit),
        failedReason: audit?.failureReason || ''
      }));
      setModifiedAudits(modified);
    } else {
      setModifiedAudits(audits);
    }
  };

  const onOrderPagination = (orderColumn: string, orderDirection: orderBy) => {
    setOrderColumn(orderColumn);
    setOrderDirection(orderDirection);
  };

  const handlePaginationChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPageNumber(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const getInternalData = (audit: IAudit) => {
    const user = users?.find((user) => user.id === parseInt(audit.userId as string, 10));
    const { firstName, lastName } = user || { firstName: '', lastName: '' };
    const site = sites?.find((site) => site.id === audit.siteId);
    return {
      firstName,
      lastName,
      siteName: site?.name || ''
    };
  };

  const auditsColumns = () => {
    const columns: IColumnType[] = [];
    _.keys(AuditTableColumn).forEach((element) => {
      const props: IColumnType = {
        fieldId: AuditId,
        name: element,
        title: AuditTableColumnTitles[element as keyof typeof AuditTableColumnTitles] || element,
        type: AuditTableColumn[element]
      };
      columns.push(props);
    });
    return columns;
  };

  if (error) return <NoInfo />;

  return (
    <PageContentContainer pageName='Audits' searchValue={searchValue} searchValueChanged={handleInputChange}>
      <PageContentTable>
        <BasicTable
          fieldId='id'
          data={modifiedAudits}
          columns={auditsColumns()}
          defaultSortBy={AuditDefaultSort}
          orderDirection={orderBy.DESC}
          onOrderPagination={onOrderPagination}
          maxLength={{ changes: 15 }}
        >
          <Pagination count={totalNumber} page={pageNumber} onChange={handlePaginationChange} />
        </BasicTable>
      </PageContentTable>
    </PageContentContainer>
  );
};

export default AuditPage;
